import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import {
  ASSESSMENT_TYPE_TO_TITLE,
  TRANSFORM_EXPLOIT_FLAG,
  categoryToTitle,
} from "@sscs/report/constants";
import {
  CommonList,
  FilterOptions,
  SDLCMetadata,
  SDLCMetadataConstants,
} from "@sscs/types";
import { GRADE_TO_PRIORITY, capitalizeFirstLetter } from "@sscs/utils";
import { APPROVAL_STATUS_MAP } from "Components";
import { PORTAL_POLICY_CATEGORY_LIST } from "Constants";
import { useLazySoftwareInfoChoicesQuery } from "Services";
import useSearch from "../../hooks/useSearch";
import {
  useLazyGetInsightsCWEsQuery,
  useLazyGetInsightsEndpointsQuery,
  useLazyGetInsightsIssuesQuery,
  useLazyGetInsightsMarketingNamesQuery,
  useLazyGetInsightsPackagesQuery,
  useLazyGetInsightsProjectsQuery,
  useLazyGetInsightsServicesQuery,
  useLazyGetInsightsSpdxIdsQuery,
  useLazyGetInsightsTagsQuery,
  useLazyGetInsightsThreatPlatformsQuery,
  useLazyGetInsightsThreatTypesQuery,
} from "../../services/insights.api";
import { InsightsFilters } from "../../types";
import { isFilterDisabled } from "../../utils";

const DIFF_REPRO_CHECK_TEXT: Record<SDLCMetadata.diff_repro_check_t, string> = {
  "repro-check-fail": "Reproducibility check failed",
  "repro-check-pass": "Reproducibility check passed",
  "repro-not-checked": "Reproducibility check not performed",
};

const MUTUALLY_EXCLUSIVE = "Mutually exclusive filter is already present";

const getCommonListLabel = (
  option: string,
  list: CommonList | undefined,
): string => list?.find((item) => item.id === option)?.value ?? option;

export const useFilterOptions = (): FilterOptions<
  keyof InsightsFilters
> | null => {
  const { groupId } = useParams();
  const { filters } = useSearch();
  const [getSoftwareInfoChoices, softwareInfoChoices] =
    useLazySoftwareInfoChoicesQuery();
  const [getComponentTags, componentTags] = useLazyGetInsightsTagsQuery();
  const [getCWEs, CWEs] = useLazyGetInsightsCWEsQuery();
  const [getEndpoints, endpoints] = useLazyGetInsightsEndpointsQuery();
  const [getIssues, issues] = useLazyGetInsightsIssuesQuery();
  const [getPackages, packages] = useLazyGetInsightsPackagesQuery();
  const [getProjects, projects] = useLazyGetInsightsProjectsQuery();
  const [getServices, services] = useLazyGetInsightsServicesQuery();
  const [getSpdxIds, spdxIds] = useLazyGetInsightsSpdxIdsQuery();
  const [getThreatPlatforms, threatPlatforms] =
    useLazyGetInsightsThreatPlatformsQuery();
  const [getThreatTypes, threatTypes] = useLazyGetInsightsThreatTypesQuery();
  const [getVulnerabilityNames, vulnerabilityNames] =
    useLazyGetInsightsMarketingNamesQuery();

  const isDisabled = useCallback(
    (name: keyof InsightsFilters): boolean => isFilterDisabled(name, filters),
    [filters],
  );

  const filterOptions = useMemo((): FilterOptions<
    keyof InsightsFilters
  > | null => {
    if (!groupId) {
      return null;
    }

    return {
      project_id: {
        name: "project_id",
        label: "Project name",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getProjects({ groupId }),
        isLoading: projects.isLoading || projects.isFetching,
        options: projects.data?.map((item) => item.id),
        isOptionDisabled: isDisabled("project_id"),
        getOptionLabel: (option) => getCommonListLabel(option, projects.data),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      package_name: {
        name: "package_name",
        label: "Package name",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getPackages({ groupId }),
        isLoading: packages.isLoading || packages.isFetching,
        options: packages.data?.map((item) => item.id),
        isOptionDisabled: isDisabled("package_name"),
        getOptionLabel: (option) => getCommonListLabel(option, packages.data),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      platform: {
        name: "platform",
        label: "Version platform",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getSoftwareInfoChoices(),
        isLoading:
          softwareInfoChoices.isLoading || softwareInfoChoices.isFetching,
        options: softwareInfoChoices.data?.platform_options,
        isOptionDisabled: isDisabled("platform"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      category: {
        name: "category",
        label: "Version category",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getSoftwareInfoChoices(),
        isLoading:
          softwareInfoChoices.isLoading || softwareInfoChoices.isFetching,
        options: softwareInfoChoices.data?.category_options,
        isOptionDisabled: isDisabled("category"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      version_is_released: {
        name: "version_is_released",
        label: "Version released",
        component: "autocomplete",
        operations: ["is"],
        options: ["True", "False"],
        isOptionDisabled: isDisabled("version_is_released"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      release_status: {
        name: "release_status",
        label: "Version status",
        component: "autocomplete",
        operations: ["is"],
        options: ["Fail", "Pass"],
        isOptionDisabled: isDisabled("release_status"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      approval_status: {
        name: "approval_status",
        label: "Version approval",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        options: Object.keys(APPROVAL_STATUS_MAP).sort(),
        getOptionLabel: (option: keyof typeof APPROVAL_STATUS_MAP) =>
          APPROVAL_STATUS_MAP[option].label,
        isOptionDisabled: isDisabled("approval_status"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      latest_released: {
        name: "latest_released",
        label: "Latest released",
        component: "autocomplete",
        operations: ["is"],
        options: ["True", "False"],
        isOptionDisabled: isDisabled("latest_released"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      reproducibility: {
        name: "reproducibility",
        label: "Reproducibility",
        component: "autocomplete",
        operations: ["is", "is_not"],
        options: [...SDLCMetadataConstants.diffReproCheck],
        getOptionLabel: (option: SDLCMetadata.diff_repro_check_t) =>
          DIFF_REPRO_CHECK_TEXT[option],
        isOptionDisabled: isDisabled("reproducibility"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      release_date: {
        name: "release_date",
        label: "Release date",
        component: "date",
        operations: ["before", "after"],
        isOptionDisabled: isDisabled("release_date"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      file_name: {
        name: "file_name",
        label: "Component name",
        component: "input",
        operations: ["is", "is_not"],
        isOptionDisabled: isDisabled("file_name"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      purl: {
        name: "purl",
        label: "Component PURL",
        component: "input",
        operations: ["is"],
        isOptionDisabled: isDisabled("purl"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      file_path: {
        name: "file_path",
        label: "Component path",
        component: "input",
        operations: ["is", "is_not"],
        isOptionDisabled: isDisabled("file_path"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      publisher: {
        name: "publisher",
        label: "Component publisher",
        component: "autocomplete",
        operations: ["is_any", "is_not_any"],
        freeSolo: true,
        placeholder: "Type and press Enter to add",
        isOptionDisabled: isDisabled("publisher"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      license: {
        name: "license",
        label: "Component license",
        component: "input",
        operations: ["is"],
        isOptionDisabled: isDisabled("license"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      tags: {
        name: "tags",
        label: "Component tags",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getComponentTags({ groupId }),
        isLoading: componentTags.isLoading || componentTags.isFetching,
        options: componentTags.data?.map((item) => item.id),
        getOptionLabel: (option) =>
          getCommonListLabel(option, componentTags.data),
        isOptionDisabled: isDisabled("tags"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      assessment_category: {
        name: "assessment_category",
        label: "SAFE assessment",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        options: Object.keys(ASSESSMENT_TYPE_TO_TITLE),
        getOptionLabel: (option: keyof typeof ASSESSMENT_TYPE_TO_TITLE) =>
          ASSESSMENT_TYPE_TO_TITLE[option],
        isOptionDisabled: isDisabled("assessment_category"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      level_blocker: {
        name: "level_blocker",
        label: "SAFE level blockers",
        component: "autocomplete",
        operations: ["is"],
        options: ["1", "2", "3", "4", "5"],
        isOptionDisabled: isDisabled("level_blocker"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      policy_category: {
        name: "policy_category",
        label: "Issue category",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        options: [...PORTAL_POLICY_CATEGORY_LIST],
        getOptionLabel: (
          option: (typeof PORTAL_POLICY_CATEGORY_LIST)[number],
        ) => categoryToTitle[option],
        isOptionDisabled: isDisabled("policy_category"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      issue_status: {
        name: "issue_status",
        label: "Issue status",
        component: "autocomplete",
        operations: ["is"],
        options: ["Fail", "Pass"],
        isOptionDisabled: isDisabled("issue_status"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      grade: {
        name: "grade",
        label: "Issue priority",
        component: "autocomplete",
        operations: ["is", "is_not", "is_any", "is_not_any"],
        // Better UX if priorities are sorted from worst (P0) to least bad (P4)
        options: SDLCMetadataConstants.softwareGrade.toReversed(),
        getOptionLabel: (option: SDLCMetadata.software_grade_t) =>
          GRADE_TO_PRIORITY[option],
        isOptionDisabled: isDisabled("grade"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      policy_id: {
        name: "policy_id",
        label: "Issue identifier",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getIssues({ groupId }),
        isLoading: issues.isLoading || issues.isFetching,
        options: issues.data?.map((item) => item.id),
        getOptionLabel: (option) => {
          const issue = issues.data?.find((item) => item.id === option);

          return issue ? `${issue.id} - ${issue.value}` : option;
        },
        getChipLabel: (option) => option,
        isOptionDisabled: isDisabled("policy_id"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      cve: {
        name: "cve",
        label: "CVE identifier",
        component: "autocomplete",
        operations: ["is_any", "is_not_any"],
        freeSolo: true,
        multiValueDelimiter: ",",
        placeholder: "Type and press Enter or , to add",
        isOptionDisabled: isDisabled("cve"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      marketing_name: {
        name: "marketing_name",
        label: "Vulnerability name",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getVulnerabilityNames({ groupId }),
        isLoading:
          vulnerabilityNames.isLoading || vulnerabilityNames.isFetching,
        options: vulnerabilityNames.data?.map((item) => item.id),
        getOptionLabel: (option) =>
          getCommonListLabel(option, vulnerabilityNames.data),
        isOptionDisabled: isDisabled("marketing_name"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      cvss_severity: {
        name: "cvss_severity",
        label: "Vulnerability severity",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        options: ["Critical", "High", "Medium", "Low"],
        isOptionDisabled: isDisabled("cvss_severity"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      exploit: {
        name: "exploit",
        label: "Vulnerability tags",
        component: "autocomplete",
        operations: ["all_of"],
        options: SDLCMetadataConstants.exploitPresence.filter(
          (exploit) => exploit !== "TRIAGED",
        ),
        getOptionLabel: (option: SDLCMetadata.exploit_presence_t) =>
          TRANSFORM_EXPLOIT_FLAG[option],
        isOptionDisabled: isDisabled("exploit"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      cwe_identifier: {
        name: "cwe_identifier",
        label: "CWE identifier",
        component: "autocomplete",
        operations: ["is", "is_any"],
        onFilterSelect: () => getCWEs({ groupId }),
        isLoading: CWEs.isLoading || CWEs.isFetching,
        options: CWEs.data?.map((item) => item.id),
        getOptionLabel: (option) => {
          const cwe = CWEs.data?.find((item) => item.id === option);

          return cwe ? `${cwe.id} - ${cwe.value}` : option;
        },
        getChipLabel: (option) => option,
        isOptionDisabled: isDisabled("cwe_identifier"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      classification: {
        name: "classification",
        label: "Malware verdict",
        component: "autocomplete",
        operations: ["is", "is_any"],
        options: ["Malicious", "Suspicious"],
        isOptionDisabled: isDisabled("classification"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      threat_platform: {
        name: "threat_platform",
        label: "Malware platform",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getThreatPlatforms({ groupId }),
        isLoading: threatPlatforms.isLoading || threatPlatforms.isFetching,
        options: threatPlatforms.data?.map((item) => item.id),
        getOptionLabel: (option) =>
          getCommonListLabel(option, threatPlatforms.data),
        isOptionDisabled: isDisabled("threat_platform"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      threat_type: {
        name: "threat_type",
        label: "Malware type",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getThreatTypes({ groupId }),
        isLoading: threatTypes.isLoading || threatTypes.isFetching,
        options: threatTypes.data?.map((item) => item.id),
        getOptionLabel: (option) =>
          getCommonListLabel(option, threatTypes.data),
        isOptionDisabled: isDisabled("threat_type"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      threat_name: {
        name: "threat_name",
        label: "Malware family",
        component: "input",
        operations: ["is"],
        isOptionDisabled: isDisabled("threat_name"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      service: {
        name: "service",
        label: "Secret identifier",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getServices({ groupId }),
        isLoading: services.isLoading || services.isFetching,
        options: services.data?.map((item) => item.id),
        getOptionLabel: (option) => getCommonListLabel(option, services.data),
        isOptionDisabled: isDisabled("service"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      endpoint: {
        name: "endpoint",
        label: "Secrets endpoint",
        component: "autocomplete",
        operations: ["is", "is_any"],
        onFilterSelect: () => getEndpoints({ groupId }),
        isLoading: endpoints.isLoading || endpoints.isFetching,
        options: endpoints.data?.map((item) => item.id),
        getOptionLabel: (option) => {
          const endpoint = endpoints.data?.find(
            (endpoint) => endpoint.id === option,
          );

          if (!endpoint) {
            return option;
          } else {
            return {
              label: endpoint.value,
              secondaryText: endpoint.id,
            };
          }
        },
        // uses option label (an object) if not overridden
        getChipLabel: (option) =>
          endpoints.data?.find((endpoint) => endpoint.id === option)?.value ??
          option,
        getChipTooltipText: (option) => option,
        isOptionDisabled: isDisabled("endpoint"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      liveness: {
        name: "liveness",
        label: "Secret liveness",
        component: "autocomplete",
        operations: ["is"],
        options: ["active", "inactive"],
        getOptionLabel: (option) => capitalizeFirstLetter(option),
        isOptionDisabled: isDisabled("liveness"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      spdx_id: {
        name: "spdx_id",
        label: "License SPDX ID",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        onFilterSelect: () => getSpdxIds({ groupId }),
        isLoading: spdxIds.isLoading || spdxIds.isFetching,
        options: spdxIds.data?.map((item) => item.id),
        getOptionLabel: (option) => getCommonListLabel(option, spdxIds.data),
        isOptionDisabled: isDisabled("spdx_id"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
      license_family: {
        name: "license_family",
        label: "License family",
        component: "autocomplete",
        operations: ["is", "is_any", "is_not", "is_not_any"],
        options: [...SDLCMetadataConstants.licenseFamily],
        isOptionDisabled: isDisabled("license_family"),
        disabledReason: MUTUALLY_EXCLUSIVE,
      },
    };
  }, [
    groupId,
    componentTags,
    getComponentTags,
    CWEs,
    getCWEs,
    endpoints,
    getEndpoints,
    issues,
    getIssues,
    packages,
    getPackages,
    projects,
    getProjects,
    services,
    getServices,
    spdxIds,
    getSpdxIds,
    softwareInfoChoices,
    getSoftwareInfoChoices,
    threatPlatforms,
    getThreatPlatforms,
    threatTypes,
    getThreatTypes,
    vulnerabilityNames,
    getVulnerabilityNames,
    isDisabled,
  ]);

  return filterOptions;
};
